/**
 * Which teacher levels can actually be placed on an inquiry.
 *
 * Placement used to hard-filter teachers to the level on the inquiry
 * (`t.level !== sel.level` → excluded), which is why McKenzie could not assign
 * herself or Molly to anything: every inquiry arrives as Standard and both of
 * them are Director. It read as a default; it was a total exclusion.
 *
 * Opening it up needs one guard, because level is not cosmetic. It sets what
 * the family pays, via tuition_tiers, and per Chris on 22 Sep the override
 * DOES change the price. Director is priced at 60 minutes only, deliberately —
 * there are no 30 or 45 minute Director tiers and none are wanted. So a
 * Director placed on a 45-minute inquiry, which is 17 of the last 20, would
 * leave the family with no price at all.
 *
 * That is the same failure as an intro lesson paying $0: a missing rate row
 * read as zero instead of as an error. Here it is caught at the moment of
 * choosing, and said out loud, rather than discovered at invoicing.
 */
/**
 * Durations a level is allowed to teach, where that is a POLICY rather than a
 * consequence of what happens to be in the price table.
 *
 * Directors teach 60-minute lessons only. Chris has stated this as an
 * established hard rule, and it is not derived from tuition_tiers — deriving it
 * meant the rule survived only as long as nobody added a Director row at 30 or
 * 45 minutes. Stated here, an accidental tier cannot quietly reinstate a
 * lesson length the studio does not sell.
 *
 * A level absent from this map has no duration policy, and is governed purely
 * by whether a price exists.
 */
const LEVEL_DURATION_POLICY: Record<string, readonly number[]> = {
  Director: [60],
};

/** Is this level allowed to teach this length at all, price aside? */
export function levelAllowedAtDuration(
  level: string | null | undefined,
  durationMinutes: number | null | undefined
): boolean {
  if (!level || !durationMinutes) return true;
  const allowed = LEVEL_DURATION_POLICY[level];
  if (!allowed) return true;
  return allowed.includes(durationMinutes);
}

export interface TuitionTierRow {
  level: string | null;
  duration_minutes: number | null;
  active?: boolean | null;
}

/** Levels with an active tuition tier at this duration. */
export function levelsPricedAt(
  tiers: TuitionTierRow[] | null | undefined,
  durationMinutes: number | null | undefined
): Set<string> {
  const out = new Set<string>();
  if (!tiers || !durationMinutes) return out;
  for (const t of tiers) {
    if (t.active === false) continue;
    if (!t.level || t.duration_minutes !== durationMinutes) continue;
    out.add(t.level);
  }
  return out;
}

/**
 * Can a teacher of this level take a lesson of this duration?
 *
 * Unknown duration or an empty tier list means we cannot tell, and blocking on
 * missing data would stop placement working at all — so it allows, and the
 * price question surfaces elsewhere.
 */
export function levelPlaceableAt(
  tiers: TuitionTierRow[] | null | undefined,
  level: string | null | undefined,
  durationMinutes: number | null | undefined
): boolean {
  if (!level || !durationMinutes) return true;
  // Policy first, and it is not overridable by data. A Director at 45 minutes
  // stays blocked even if someone adds a 45-minute Director tier by mistake.
  if (!levelAllowedAtDuration(level, durationMinutes)) return false;
  const priced = levelsPricedAt(tiers, durationMinutes);
  if (priced.size === 0) return true;
  return priced.has(level);
}

/** What to tell whoever just tried to pick them. */
export function levelBlockReason(
  level: string | null | undefined,
  durationMinutes: number | null | undefined
): string {
  const allowed = LEVEL_DURATION_POLICY[level || ''];
  if (allowed) {
    const list = allowed.map((d) => `${d}-minute`).join(' or ');
    return `${level} teaches ${list} lessons only. Change the lesson length or pick another teacher.`;
  }
  return `${level} has no ${durationMinutes}-minute rate, so this family would have no price. Change the lesson length or pick another teacher.`;
}

/**
 * Every lesson length this level can actually be sold at, out of the lengths
 * the studio offers.
 *
 * This exists so the placement screen never has to DISABLE anything. Given a
 * level, it can snap the length to one that works instead of greying out the
 * teacher and leaving the admin to work out why. Director returns [60], which
 * is the whole reason the Anna Barbour inquiry sat unplaceable.
 */
export function allowedDurationsForLevel(
  tiers: TuitionTierRow[] | null | undefined,
  level: string | null | undefined,
  choices: readonly number[]
): number[] {
  return choices.filter((d) => levelPlaceableAt(tiers, level, d));
}

/**
 * The length closest to the one already chosen that this level can take.
 * Closest, not first, so correcting a 45 to a 60 does not overshoot to a 30.
 */
export function nearestAllowedDuration(
  tiers: TuitionTierRow[] | null | undefined,
  level: string | null | undefined,
  current: number | null | undefined,
  choices: readonly number[]
): number | null {
  const allowed = allowedDurationsForLevel(tiers, level, choices);
  if (!allowed.length) return null;
  if (current && allowed.includes(current)) return current;
  if (!current) return allowed[0];
  return allowed.reduce((best, d) => (Math.abs(d - current) < Math.abs(best - current) ? d : best));
}

/**
 * Placing a teacher whose level differs from the one the inquiry was quoted at
 * changes what the family pays. That is intended, and it is worth saying.
 */
export function levelChangesPrice(
  requestedLevel: string | null | undefined,
  teacherLevel: string | null | undefined
): boolean {
  if (!requestedLevel || !teacherLevel) return false;
  return requestedLevel !== teacherLevel;
}
